import { useEffect, useRef } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useAppSelector } from "../Redux/Store/Store";
import { getAiPowered, getClient } from "../Redux/Store/Slices/client";
import { warmupMcpAgent } from "../API/McpChat/McpChat";

export const MCP_AUDIENCE = "https://api.mesma.co.uk";

/**
 * Pre-connect MCP as soon as an AI-powered client is known.
 * Pass conversationId on Analyse to also cache that chat's agent.
 * Soft-fails — chat still works on demand.
 */
export function useMcpWarmup(conversationId?: string) {
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();
  const client = useAppSelector(getClient);
  const aiPowered = useAppSelector(getAiPowered);
  const abortRef = useRef<AbortController | null>(null);

  const clientId = client?.id?.trim();
  const chatId = conversationId?.trim() || undefined;

  useEffect(() => {
    if (!isAuthenticated || !aiPowered) {
      return;
    }

    if (!clientId || clientId === "undefined" || clientId === "null") {
      return;
    }

    // App bootstrap: fire immediately. Chat switch: brief debounce to avoid abort storms.
    const delayMs = chatId ? 300 : 0;
    const timer = window.setTimeout(async () => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      try {
        const token = await getAccessTokenSilently({
          authorizationParams: { audience: MCP_AUDIENCE },
        });
        await warmupMcpAgent(token, clientId, chatId, controller.signal);
      } catch (error) {
        if (
          (error as { name?: string })?.name === "CanceledError" ||
          (error as { code?: string })?.code === "ERR_CANCELED" ||
          (error as { name?: string })?.name === "AbortError"
        ) {
          return;
        }
        console.debug("[Mia warmup] skipped or failed", error);
      }
    }, delayMs);

    return () => {
      window.clearTimeout(timer);
      abortRef.current?.abort();
    };
  }, [isAuthenticated, aiPowered, clientId, chatId, getAccessTokenSilently]);
}
